import { css } from "@emotion/css"
import { useCallback, useState } from "react"

import CircleHeader from "@p-ban/common/src/components/CircleHeader"
import CloseButton from "@p-ban/common/src/components/button/CloseButton/CloseButton"
import Modal from "@p-ban/common/src/components/lib/Modal"
import useArrayState from "@p-ban/common/src/hooks/useArrayState"

import notify, { clearPrevNotification } from "../../../../../../utils/notify"
import estimateField from "../../../../service/estimateFields/estimateField"
import type { SetFieldValue } from "../../../../types"
import type { EstimateParams } from "../../../../types/params"
import COLOR_CODE from "../../../constant/color"
import MODAL_ID from "../../../constant/modalId"
import { useModal } from "../useModal"
import Column from "./Column"
import ColumnTemplate from "./ColumnTemplate"

const useSimulation = (setCountToValues: (count: number | null) => void) => {
  const { isOpen, close } = useModal(MODAL_ID.MULTIPLE_COUNT_SIMULATION)

  const initialValue: (number | null)[] = [20, 55, 255, 1000]
  const counts = useArrayState(initialValue)

  const [didClickSelect, setDidClickSelect] = useState(false)
  const onAfterOpen = () => {
    clearPrevNotification?.()
    setDidClickSelect(false)
  }
  const selectAndClose = (count: number | null) => {
    close()
    setDidClickSelect(true)
    setCountToValues(count)
  }
  const onAfterClose = () => {
    if (didClickSelect) {
      notify("製造枚数を変更しました。")
    }
  }

  return { isOpen, close, onAfterOpen, onAfterClose, counts, selectAndClose }
}

const MultipleCountSimulation = ({
  params,
  setFieldValue,
}: {
  params: EstimateParams
  setFieldValue: SetFieldValue
}) => {
  const setCountToValues = useCallback(
    (count: number | null) => setFieldValue(estimateField.cnt.key, count),
    [setFieldValue]
  )
  const { isOpen, close, onAfterOpen, onAfterClose, counts, selectAndClose } = useSimulation(setCountToValues)

  return (
    <Modal
      contentLabel="枚数シミュレーション"
      isOpen={isOpen}
      onAfterOpen={onAfterOpen}
      onAfterClose={onAfterClose}
      onRequestClose={close}
    >
      <div className={styles.container}>
        <div>
          <CircleHeader color={COLOR_CODE.SIMULATION_CIRCLE_BLUE}>枚数シミュレーション</CircleHeader>
          <div className={styles.flex}>
            <ColumnTemplate mapChildren>
              <div className={styles.courseName}>ウルトラクイック{"\n"}コース</div>
              <div className={styles.courseName}>スーパークイック{"\n"}コース</div>
              <div className={styles.courseName}>クイック{"\n"}コース</div>
              <div className={styles.courseName}>ノーマル{"\n"}コース</div>
              <div className={styles.courseName}>量産{"\n"}コース</div>
            </ColumnTemplate>
            <Column params={params} count={params.values.cnt} readOnly />
            {counts.map(([count, setCount], i) => (
              <Column
                key={i}
                params={params}
                count={count}
                setCount={setCount}
                onButtonClick={() => selectAndClose(count)}
              />
            ))}
          </div>
        </div>
        <div>
          <CloseButton onClick={close} />
        </div>
      </div>
    </Modal>
  )
}

export default MultipleCountSimulation

const styles = {
  container: css`
    display: flex;
    background-color: ${COLOR_CODE.WHITE};
  `,
  courseName: css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    font-size: 13px;
    font-weight: bold;
    text-align: center;
    white-space: pre-wrap;
    background-color: ${COLOR_CODE.SIMULATION_COURSE_GRAY};
  `,
  flex: css`
    display: flex;
    padding-top: 20px;
    padding-right: 20px;
    > * {
      :not(:last-child) {
        border-right: none;
      }
    }
  `,
}
